import { HttpClient } from '@angular/common/http';
import { EventEmitter, Injectable, Output } from '@angular/core';
import { HttpTransportType, HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import { HttpService } from 'src/app/core/data/service/http.service';
import { ISocialLoginRequest, IActivateRequest, ILoginResponse, IResetRequest, IChangePasswordRequest } from '../schema/auth-interfaces';
import { LoginRequest, RegisterRequest } from '../schema/auth-models';
import { Profile } from '../schema/profile';
import { AppSettings } from 'src/app/core/configs/app.settings';
import { AuctionService } from 'src/app/data/service/auction.service';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService extends HttpService {

  @Output() 'loginStateChanged': EventEmitter<number> = new EventEmitter();

  private connection: HubConnection;
  private didDisconnect = false;
  private busy = false;

  private jwt: string;
  get token() { return this.jwt; }

  private userInfo: Profile = new Profile();
  get profile() { return this.userInfo; }

  get socketConnected() { return (this.connection != null && this.connection.state === HubConnectionState.Connected); }

  constructor(http: HttpClient) {
    super(http);
    this.endpoint = `${this.apiURL}/auth`;
    this.autoLogin();
  }

  autoLogin() {
    const authStr = localStorage.getItem('last-auth') || '{}';
    const authResult = JSON.parse(authStr);
    // Test if we have a token and if the expiration > now
    if (authResult.token && new Date(authResult.tokenExpires) > new Date()) {
      this.storeLoginResult(authResult);
      this.fetchProfile();
      console.log('Auto-login successful.');
    } else {
      console.log('Auto-login failed. No or Expired token.');
    }
  }

  isLoginValid() {
    const authStr = localStorage.getItem('last-auth') || '{}';
    const authResult = JSON.parse(authStr);

    return (authResult.token && new Date(authResult.tokenExpires) > new Date()) ? true : false;
  }

  logout() {
    AuctionService.communityId = undefined;
    this.loginStateChanged.emit(0);
    console.log('Logging out..');
    localStorage.removeItem('last-auth');
    localStorage.removeItem('cbsjwt');
    this.jwt = undefined;
    this.userInfo = new Profile();
  }

  async login(details: LoginRequest) {
    try {
      return await this.runLogin(details, `${this.endpoint}/login`);
    } catch (error) {
      console.error('Login error: ' + error);
      throw error;
    }
  }

  async socialLogin(details: ISocialLoginRequest) {
    try {
      return await this.runLogin(details, `${this.endpoint}/login/${details.provider}`);
    } catch (error) {
      console.error('Error social login', error);
      throw error || 'Error logging in.';
    }
  }

  async tokenLogin(cbsToken: string) {
    try {
      let rslt = await this.runLogin({ token: cbsToken, provider: 'CBS' }, `${this.endpoint}/cbs`);
      console.log('external login succeeded?', rslt);
      return rslt;
    } catch (error) {
      console.error('Login error: ' + error);
      throw error;
    }
  }

  async register(profile: RegisterRequest) {
    try {
      // Run the register request, posting the data
      return await this.post(profile, `${this.endpoint}/register`);
    } catch (error) {
      console.error('Error register', error);
      throw error || 'Error registering account.';
    }
  }

  async resendActivation(email: string) {
    try {
      // Run the resend activation, posting the data
      await this.post({ value: email }, `${this.endpoint}/activation/send`, 'text');
    } catch (error) {
      console.error('Error resent activation', error);
      throw error || 'Error requesting activation email.';
    }
  }

  async requestReset(request: IResetRequest) {
    try {
      // Run the request reset, posting the data
      await this.post(request, `${this.endpoint}/requestreset`, 'text');
    } catch (error) {
      console.error('Error request reset', error);
      throw error || 'Error requesting password reset.';
    }
  }

  async resetPassword(request: IChangePasswordRequest) {
    try {
      // Run the reset request, posting the data
      await this.post(request, `${this.endpoint}/passwordreset`, 'text');
    } catch (error) {
      console.error('Error reset pwd', error);
      throw error || 'Error resetting password.';
    }
  }

  async activateAccount(request: IActivateRequest) {
    try {
      // Run the activate request, posting the data
      await this.post(request, `${this.endpoint}/activate`, 'text');
    } catch (error) {
      console.error('Error activating', error);
      throw error || 'Error activating account.';
    }
  }

  async getCBSSsoToken() {
    try {
     let url = `${this.endpoint}/sso/generate/bidbeacon`;
      if (AuctionService.auctionId) {
        url += `/${AuctionService.auctionId}`;
      }
      return await this.fetch(url, 'text');
    } catch (error) {
      console.error('Error getting CBS SSO token', error);
      throw error || 'Error getting CBS SSO token.';
    }
  }

  updateProfile(profile: Profile) {
    this.userInfo = profile;
    // Load the stored result. Replace the profile, and save it again
    const authStr = localStorage.getItem('last-auth') || '{}';
    const authResult = JSON.parse(authStr);
    authResult.profile = profile;
    localStorage.setItem('last-auth', JSON.stringify(authResult));
  }

  private async runLogin(data: LoginRequest | ISocialLoginRequest, endpoint: string = this.endpoint) {
    try {
      // Run the login request, posting the data
      const loginResp = await this.post(data, endpoint) as ILoginResponse;
      this.storeLoginResult(loginResp);
    } catch (error) {
      console.error('Error during login', error);
      throw error || 'Error during login.';
    }
  }

  private storeLoginResult(result: ILoginResponse) {
    // set local properties
    this.jwt = result.token;
    this.userInfo = result.profile;
    // set the storage
    localStorage.setItem('last-auth', JSON.stringify(result));
    this.loginStateChanged.emit(1);
    // console.log('stored login result', result);

    // Only run this if we haven't confirmed the email
    if (this.profile.emailConfirmed === false) {
      // Run the activation check
      try {
        this.checkActivation()
          .catch(error => console.error('Error checking activation', error));
      } catch (error) {
        console.error('Error checking activation', error);
      }
    }
  }

  private async connectToHub() {
    if (this.connection == null) {
      // Create the connection if one doesn't exist
      this.connection = new HubConnectionBuilder()
        .withUrl(AppSettings.hubURL, {
          skipNegotiation: true,
          transport: HttpTransportType.WebSockets
        })
        .configureLogging(LogLevel.Information)
        .build();

      // Setup the events
      this.connection.onclose(error => this.hubClosed(error));
      // Auction events
      this.connection.on('Activate', data => {
        console.log(data);
        this.completeActivation();
      });
    }
    if (this.socketConnected === false && this.busy === false) {
      try {
        // Start the connection if we aren't already connected
        this.busy = true;
        await this.connection.start();
        await this.connection.send('ActivateCheck', this.profile.userId);
        this.didDisconnect = false;
      } catch (error) {
        console.error('Error connecting to auction hub.', error);
        throw error;
      } finally {
        this.busy = false;
      }
    }
  }

  private hubClosed(error: Error) {
    console.error('Socket connection closed.', error);
    if (this.didDisconnect === false) {
      this.runReconnect();
    }
  }

  private runReconnect() {
    let retries = 3;
    const reconnectInt = setInterval(async () => {
      if (retries > 0) {
        await this.connectToHub();
        if (this.socketConnected === true) {
          clearInterval(reconnectInt);
        }
        retries -= 1;
      } else {
        clearInterval(reconnectInt);
      }
    }, 2000);
  }

  private completeActivation() {
    this.profile.emailConfirmed = true;
    this.updateProfile(this.profile);
    if (this.socketConnected) {
      this.connection.send('DisconnectActivateCheck', this.profile.userId);
      this.didDisconnect = true;
      this.connection.stop();
    }
  }

  private async checkActivation() {
    try {
      const response = await this.fetch(`${this.endpoint}/activate/check`) as boolean;
      if (response === true) {
        this.completeActivation();
      } else {
        // If no alert, then we called this on run. So do signalr if we can.
        this.connectToHub();
      }
    } catch (error) {
      console.error('Error checking activation', error);
      throw error || 'Error checking account activation.';
    }
  }

  async fetchProfile() {
    try {
      const profile = new Profile(await this.fetch(`${this.apiURL}/users/profile`));
      this.updateProfile(profile);
    } catch (error) {
      console.error('Error fetching user profile. Error:', error);
    }
  }

  protected getUpdateUrl(entity: any): string {
    throw new Error('Method not implemented.');
  }

  protected isUpdate(entity: any): boolean {
    throw new Error('Method not implemented.');
  }

}
